
import posState from "./posState";

const { state } = posState;

// ============================================================
// SALE / CHECKOUT STATE INVALIDATION
// ============================================================

function invalidateSaleState() {
  state.sale = null;
  state.checkout = null;
  state.checkoutDialog = false;
  state.paymentMethod = null;
  state.paymentTransaction = null;
  state.paymentStatus = null;
  state.paymentError = null;
  state.processingPayment = false;
  state.stripeCheckout = null;
  state.stripeCheckoutSessionId = null;
  state.stripePaymentTransactionId = null;
  state.stripePolling = false;
  state.receipt = null;
}

// ============================================================
// CURRENCY
//
// The active POS session is authoritative.
// Currency validation must happen at runtime, never at module
// initialization, because the session loads asynchronously.
// ============================================================

function resolveSessionCurrency() {
  const sessionCurrency = String(
    state.session?.currencyCode ??
    state.session?.currency ??
    ""
  )
    .trim()
    .toUpperCase();

  if (!/^[A-Z]{3}$/.test(sessionCurrency)) {
    console.error("[POS] Invalid session currency:", {
      session: state.session,
      stateCurrency: state.currency
    });

    throw new Error(
      "The active POS session has no valid currency."
    );
  }

  return sessionCurrency;
}

function setCurrency(currency) {
  const sessionCurrency = resolveSessionCurrency();

  const requestedCurrency = String(
    currency ?? sessionCurrency
  )
    .trim()
    .toUpperCase();

  if (requestedCurrency !== sessionCurrency) {
    throw new Error(
      `This POS session is locked to ${sessionCurrency}. ` +
      `Transactions in ${requestedCurrency} are not permitted.`
    );
  }

  const currentCurrency = String(
    state.currency ?? ""
  )
    .trim()
    .toUpperCase();

  if (currentCurrency === sessionCurrency) {
    state.currency = sessionCurrency;
    return;
  }

  state.currency = sessionCurrency;
  invalidateSaleState();
}

// ============================================================
// DISCOUNT
// ============================================================

function setDiscountRate(rate) {
  const normalized = normalizeNullableNumber(rate);

  if (state.discountRate === normalized) {
    return;
  }

  state.discountRate = normalized;

  if (normalized !== null) {
    state.discountAmount = null;
  }

  invalidateSaleState();
}

function setDiscountAmount(amount) {
  const normalized = normalizeNullableMoney(amount);

  if (state.discountAmount === normalized) {
    return;
  }

  state.discountAmount = normalized;

  if (normalized !== null) {
    state.discountRate = null;
  }

  invalidateSaleState();
}

// ============================================================
// TAX
// ============================================================

function setTaxCodes(taxCodes) {
  state.taxCodes = Array.isArray(taxCodes)
    ? taxCodes
    : [];

  if (state.taxCodeId) {
    state.taxCode =
      state.taxCodes.find(
        item =>
          String(item.id ?? item.taxCodeId) ===
          String(state.taxCodeId)
      ) ?? null;
  }
}

function setTaxCode(taxCodeId) {
  const normalizedId = taxCodeId ?? null;
  const currentId = state.taxCodeId ?? null;

  const sameTaxCode =
    (normalizedId === null && currentId === null) ||
    (
      normalizedId !== null &&
      currentId !== null &&
      String(normalizedId) === String(currentId)
    );

  if (sameTaxCode) {
    return;
  }

  state.taxCodeId = normalizedId;

  state.taxCode =
    normalizedId === null
      ? null
      : (
          state.taxCodes.find(
            item =>
              String(item.id ?? item.taxCodeId) ===
              String(normalizedId)
          ) ?? null
        );

  invalidateSaleState();
}

// ============================================================
// TIP
// ============================================================

function setTipRate(rate) {
  const normalized = normalizeNullableNumber(rate);

  if (state.tipRate === normalized) {
    return;
  }

  state.tipRate = normalized;

  if (normalized !== null) {
    state.tipAmount = null;
  }

  invalidateSaleState();
}

function setTipAmount(amount) {
  const normalized = normalizeNullableMoney(amount);

  if (state.tipAmount === normalized) {
    return;
  }

  state.tipAmount = normalized;

  if (normalized !== null) {
    state.tipRate = null;
  }

  invalidateSaleState();
}

// ============================================================
// CUSTOMER
// ============================================================

function setCustomer(customer) {
  state.customer = customer ?? null;
  invalidateSaleState();
}

// ============================================================
// LOYALTY MEMBERSHIP
//
// Independent of POS customer.
// Membership cannot change after sale creation or during payment.
// ============================================================

function assertLoyaltyEditable() {
  if (
    state.sale?.id ||
    state.creatingSale ||
    state.checkingOut ||
    state.processingPayment ||
    state.stripePolling ||
    ["PENDING", "PROCESSING"].includes(
      String(state.paymentStatus ?? "").toUpperCase()
    )
  ) {
    throw new Error(
      "The loyalty selection cannot be changed after sale creation or during payment."
    );
  }
}

function setMembership(member) {
  assertLoyaltyEditable();

  if (
    member &&
    (
      !member.membershipId ||
      member.isEligible !== true
    )
  ) {
    throw new Error(
      "Select an eligible membership with a valid membership ID."
    );
  }

  const nextMembershipId =
    member?.membershipId ?? null;

  const membershipChanged =
    String(state.membershipId ?? "") !==
    String(nextMembershipId ?? "");

  state.membershipId = nextMembershipId;

  state.membership = member
    ? JSON.parse(JSON.stringify(member))
    : null;

  // A previously selected program must not be carried over
  // to another member or retained after membership removal.
  if (membershipChanged || !nextMembershipId) {
    state.loyaltyProgramId = null;
  }
}

// ============================================================
// LOYALTY PROGRAM
//
// Program must belong to the selected membership.
// Backend remains authoritative for enrollment, POS eligibility
// and terminal-location assignment.
// ============================================================

function setLoyaltyProgram(programId) {
  assertLoyaltyEditable();

  const normalizedId =
    typeof programId === "object" && programId !== null
      ? (
          programId.loyaltyProgramId ??
          programId.id ??
          null
        )
      : (programId ?? null);

  if (normalizedId && !state.membershipId) {
    throw new Error(
      "Select a loyalty membership before selecting a loyalty program."
    );
  }

  if (
    normalizedId &&
    String(normalizedId) ===
      "00000000-0000-0000-0000-000000000000"
  ) {
    throw new Error(
      "Select a valid loyalty program."
    );
  }

  state.loyaltyProgramId = normalizedId;
}

// ============================================================
// BUILD SALE PAYLOAD
//
// Currency is resolved when the sale is being created.
// Never validate session currency outside this function
// at module initialization.
// ============================================================

function buildSalePayload(options = {}) {
  const terminalId =
    state.terminal?.id ??
    state.terminal?.terminalId ??
    null;

  const sessionId =
    options.sessionId ??
    state.session?.id ??
    state.session?.sessionId ??
    null;

  if (!terminalId) {
    throw new Error(
      "A POS terminal is required to create a sale."
    );
  }

  if (!sessionId) {
    throw new Error(
      "An active POS session is required to create a sale."
    );
  }

  if (
    !Array.isArray(state.cart) ||
    state.cart.length === 0
  ) {
    throw new Error(
      "At least one cart item is required to create a sale."
    );
  }

  if (
    state.membershipId &&
    !state.loyaltyProgramId
  ) {
    throw new Error(
      "Select a loyalty program for the selected membership."
    );
  }

  // ----------------------------------------------------------
  // SESSION CURRENCY
  //
  // Resolve at runtime after the active session is available.
  // The backend session currency is authoritative.
  // ----------------------------------------------------------

  const sessionCurrency = resolveSessionCurrency();

  // Keep frontend state synchronized with the session.
  // Do not invalidate the sale here; payload construction
  // should not reset payment or checkout state.
  state.currency = sessionCurrency;

  // ----------------------------------------------------------
  // SALE ITEMS
  // ----------------------------------------------------------

  const items = state.cart.map(item => ({
    itemId: item.itemId,
    quantity: Number(item.quantity ?? 0),
    unitPrice: Number(item.unitPrice ?? 0)
  }));

  // ----------------------------------------------------------
  // REQUEST PAYLOAD
  // ----------------------------------------------------------

  return {
    terminalId,
    sessionId,

    customerId:
      state.customer?.id ??
      state.customer?.customerId ??
      null,

    membershipId:
      state.membershipId ??
      state.membership?.membershipId ??
      null,

    loyaltyProgramId:
      state.loyaltyProgramId ?? null,

    currency: sessionCurrency,

    discountRate:
      normalizeNullableNumber(state.discountRate),

    discountAmount:
      normalizeNullableMoney(state.discountAmount),

    taxCodeId:
      state.taxCodeId ?? null,

    tipRate:
      normalizeNullableNumber(state.tipRate),

    tipAmount:
      normalizeNullableMoney(state.tipAmount),

    wasCreatedOffline:
      Boolean(options.wasCreatedOffline),

    clientSaleDateUtc:
      options.clientSaleDateUtc ??
      new Date().toISOString(),

    notes:
      options.notes ?? null,

    items
  };
}

// ============================================================
// APPLY SALE
// ============================================================

function applySale(sale) {
  if (!sale || !sale.id) {
    throw new Error(
      "A valid backend POS sale is required."
    );
  }

  state.sale = sale;

  // Backend values become authoritative.

  if (sale.currency) {
    state.currency = String(sale.currency)
      .trim()
      .toUpperCase();
  }

  if (sale.discountRate !== undefined) {
    state.discountRate =
      normalizeNullableNumber(sale.discountRate);
  }

  if (sale.discountAmount !== undefined) {
    state.discountAmount =
      normalizeNullableMoney(sale.discountAmount);
  }

  if (sale.taxCodeId !== undefined) {
    state.taxCodeId = sale.taxCodeId ?? null;
  }

  if (sale.tipRate !== undefined) {
    state.tipRate =
      normalizeNullableNumber(sale.tipRate);
  }

  if (sale.tipAmount !== undefined) {
    state.tipAmount =
      normalizeNullableMoney(sale.tipAmount);
  }

  return sale;
}

// ============================================================
// AUTHORITATIVE SALE TOTALS
// ============================================================

function getSaleTotals() {
  const sale = state.sale;

  if (!sale?.id) {
    return null;
  }

  const subtotal =
    normalizeMoney(sale.subtotal);

  const discountAmount =
    normalizeMoney(sale.discountAmount);

  const taxAmount =
    normalizeMoney(sale.taxAmount);

  const tipAmount =
    normalizeMoney(sale.tipAmount);

  const totalAmount =
    normalizeMoney(sale.totalAmount);

  const paidAmount =
    normalizeMoney(sale.paidAmount);

  const outstandingAmount =
    sale.outstandingAmount !== null &&
    sale.outstandingAmount !== undefined
      ? normalizeMoney(sale.outstandingAmount)
      : Math.max(
          0,
          roundMoney(totalAmount - paidAmount)
        );

  return {
    subtotal,

    discountRate:
      normalizeNullableNumber(sale.discountRate),

    discountAmount,

    taxCodeId:
      sale.taxCodeId ?? null,

    taxCode:
      sale.taxCode ?? null,

    taxRate:
      normalizeNullableNumber(sale.taxRate),

    taxAmount,

    tipRate:
      normalizeNullableNumber(sale.tipRate),

    tipAmount,

    totalAmount,
    paidAmount,
    outstandingAmount,

    currency:
      String(
        sale.currency ??
        state.session?.currencyCode ??
        state.session?.currency ??
        state.currency ??
        ""
      )
        .trim()
        .toUpperCase()
  };
}

// ============================================================
// APPLY CHECKOUT
// ============================================================

function applyCheckout(checkout) {
  if (!checkout) {
    throw new Error(
      "A valid checkout response is required."
    );
  }

  state.checkout = checkout;
  return checkout;
}

// ============================================================
// CAN CHECKOUT
// ============================================================

function canCheckout() {
  const terminalId =
    state.terminal?.id ??
    state.terminal?.terminalId ??
    null;

  const sessionId =
    state.session?.id ??
    state.session?.sessionId ??
    null;

  if (!terminalId || !sessionId) {
    return false;
  }

  if (!state.sale?.id) {
    return false;
  }

  if (
    state.processingPayment ||
    state.checkingOut
  ) {
    return false;
  }

  const totals = getSaleTotals();

  if (!totals) {
    return false;
  }

  return totals.outstandingAmount > 0;
}

// ============================================================
// OPEN CHECKOUT
// ============================================================

function openCheckout() {
  if (!canCheckout()) {
    throw new Error(
      "The POS sale is not ready for checkout."
    );
  }

  state.checkoutDialog = true;
}

// ============================================================
// CLOSE CHECKOUT
// ============================================================

function closeCheckout() {
  if (state.processingPayment) {
    return;
  }

  state.checkoutDialog = false;
  state.paymentError = null;
}

// ============================================================
// PAYMENT METHOD
// ============================================================

function setPaymentMethod(method) {
  state.paymentMethod = method
    ? String(method).trim().toLowerCase()
    : null;

  state.paymentError = null;
}

// ============================================================
// PAYMENT TRANSACTION
// ============================================================

function setPaymentTransaction(transaction) {
  state.paymentTransaction = transaction ?? null;
}

// ============================================================
// PAYMENT STATUS
// ============================================================

function setPaymentStatus(status) {
  state.paymentStatus = status
    ? String(status).trim().toUpperCase()
    : null;
}

// ============================================================
// NORMALIZE NULLABLE NUMBER
// ============================================================

function normalizeNullableNumber(value) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return null;
  }

  const numeric = Number(value);

  return Number.isFinite(numeric)
    ? numeric
    : null;
}

// ============================================================
// NORMALIZE NULLABLE MONEY
// ============================================================

function normalizeNullableMoney(value) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return null;
  }

  const numeric = Number(value);

  if (!Number.isFinite(numeric)) {
    return null;
  }

  return roundMoney(numeric);
}

// ============================================================
// NORMALIZE MONEY
// ============================================================

function normalizeMoney(value) {
  const numeric = Number(value ?? 0);

  if (!Number.isFinite(numeric)) {
    return 0;
  }

  return roundMoney(numeric);
}

// ============================================================
// ROUND MONEY
// ============================================================

function roundMoney(value) {
  return Math.round(
    (Number(value) + Number.EPSILON) * 100
  ) / 100;
}

// ============================================================
// EXPORT
// ============================================================

export default {
  // CURRENCY
  setCurrency,

  // DISCOUNT
  setDiscountRate,
  setDiscountAmount,

  // TAX
  setTaxCodes,
  setTaxCode,

  // TIP
  setTipRate,
  setTipAmount,

  // CUSTOMER
  setCustomer,

  // LOYALTY
  setMembership,
  setLoyaltyProgram,

  // SALE
  buildSalePayload,
  applySale,
  getSaleTotals,

  // CHECKOUT
  applyCheckout,
  canCheckout,
  openCheckout,
  closeCheckout,

  // PAYMENT
  setPaymentMethod,
  setPaymentTransaction,
  setPaymentStatus
};
